import { Mesh } from '@babylonjs/core/Meshes/mesh'
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import { Quaternion } from '@babylonjs/core/Maths/math.vector'
import { TransformNode } from '@babylonjs/core/Meshes/transformNode'
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData'
import '@babylonjs/core/Meshes/instancedMesh'
import type { InstancedMesh } from '@babylonjs/core/Meshes/instancedMesh'
import type { Scene } from '@babylonjs/core/scene'
import type { TrackChunkDefinition } from '../world/ChunkTypes'
import { sampleObstaclePlacement } from '../world/ObstacleBehavior'
import { positionOnTrack, sampleChunkFrame } from '../world/TrackFrame'
import type { MaterialLibrary } from './MaterialLibrary'

const MAX_VISIBLE_CHUNKS = 14
const MAX_OBSTACLES = 6
const TUNNEL_FRAMES = 6
const SUPPORTS = 7
const RIBBON_DIVISIONS = 14

function updateRibbon(
  mesh: Mesh,
  chunk: TrackChunkDefinition,
  start: number,
  end: number,
  left: number,
  right: number,
): void {
  const length = end - start
  mesh.setEnabled(length > 0.25)
  if (length <= 0.25) return
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const indices: number[] = []
  for (let index = 0; index <= RIBBON_DIVISIONS; index += 1) {
    const distance = start + length * index / RIBBON_DIVISIONS
    const frame = sampleChunkFrame(chunk, distance)
    const leftPoint = positionOnTrack(chunk, distance, left)
    const rightPoint = positionOnTrack(chunk, distance, right)
    positions.push(leftPoint.x, leftPoint.y, leftPoint.z - chunk.startZ)
    positions.push(rightPoint.x, rightPoint.y, rightPoint.z - chunk.startZ)
    normals.push(frame.up.x, frame.up.y, frame.up.z, frame.up.x, frame.up.y, frame.up.z)
    uvs.push(0, distance * 0.08, 1, distance * 0.08)
    if (index < RIBBON_DIVISIONS) {
      const base = index * 2
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2)
    }
  }
  const data = new VertexData()
  data.positions = positions
  data.normals = normals
  data.uvs = uvs
  data.indices = indices
  data.applyToMesh(mesh, true)
  mesh.refreshBoundingInfo()
}

class HazardVisual {
  private readonly root: TransformNode
  private readonly body: Mesh
  private readonly armor: Mesh
  private readonly energy: Mesh
  private readonly emitterA: Mesh
  private readonly emitterB: Mesh
  private debugEnabled = false

  public constructor(name: string, scene: Scene, materials: MaterialLibrary, parent: TransformNode) {
    this.root = new TransformNode(`${name}-root`, scene)
    this.root.parent = parent
    this.body = MeshBuilder.CreateBox(`${name}-body`, { size: 1, wrap: true }, scene)
    this.body.material = materials.obstacle
    this.body.parent = this.root
    this.armor = MeshBuilder.CreateBox(`${name}-armor`, { size: 1 }, scene)
    this.armor.material = materials.dark
    this.armor.parent = this.root
    this.energy = MeshBuilder.CreateBox(`${name}-energy`, { size: 1 }, scene)
    this.energy.material = materials.edge
    this.energy.parent = this.root
    this.emitterA = MeshBuilder.CreateCylinder(`${name}-emitter-a`, { height: 1, diameter: 1, tessellation: 18 }, scene)
    this.emitterB = this.emitterA.clone(`${name}-emitter-b`)
    this.emitterA.material = materials.edge
    this.emitterB.material = materials.edge
    this.emitterA.parent = this.root
    this.emitterB.parent = this.root
    this.root.setEnabled(false)
  }

  public assign(chunk: TrackChunkDefinition, slotIndex: number, tick: number): void {
    const slot = chunk.obstacles[slotIndex]
    if (slot === undefined) {
      this.root.setEnabled(false)
      return
    }
    this.root.setEnabled(true)
    const placement = sampleObstaclePlacement(chunk, slot, tick)
    this.root.position.set(placement.position.x, placement.position.y, placement.position.z - chunk.startZ)
    this.root.rotationQuaternion = new Quaternion(
      placement.rotation.x,
      placement.rotation.y,
      placement.rotation.z,
      placement.rotation.w,
    )
    this.body.scaling.set(slot.width, slot.height, slot.depth)
    this.body.showBoundingBox = this.debugEnabled
    this.armor.scaling.set(slot.width * 1.08, Math.max(0.16, slot.height * 0.1), slot.depth * 1.32)
    this.armor.position.y = slot.height * 0.32
    this.energy.scaling.set(slot.width * 0.82, Math.max(0.055, slot.height * 0.025), slot.depth * 1.38)
    this.energy.position.y = -slot.height * 0.18
    const emitterScale = Math.min(0.72, slot.height * 0.18)
    for (const [emitter, side] of [[this.emitterA, -1], [this.emitterB, 1]] as const) {
      emitter.scaling.set(emitterScale, slot.depth * 1.38, emitterScale)
      emitter.rotation.x = Math.PI / 2
      emitter.position.set(side * slot.width * 0.47, 0, 0)
    }
  }

  public release(): void {
    this.root.setEnabled(false)
  }

  public setColliderDebug(enabled: boolean): void {
    this.debugEnabled = enabled
    this.body.showBoundingBox = enabled
  }
}

class ChunkVisual {
  public chunk: TrackChunkDefinition | null = null
  private readonly root: TransformNode
  private readonly platforms: readonly Mesh[]
  private readonly rails: readonly Mesh[]
  private readonly supports: readonly Mesh[]
  private readonly obstacles: readonly HazardVisual[]
  private readonly tunnelFrames: readonly Mesh[]

  public constructor(index: number, scene: Scene, materials: MaterialLibrary) {
    this.root = new TransformNode(`chunk-root-${index}`, scene)
    this.platforms = [0, 1].map((part) => {
      const mesh = new Mesh(`track-ribbon-${index}-${part}`, scene)
      mesh.material = materials.track
      mesh.parent = this.root
      mesh.isPickable = false
      return mesh
    })
    this.rails = [0, 1, 2, 3].map((rail) => {
      const mesh = new Mesh(`edge-ribbon-${index}-${rail}`, scene)
      mesh.material = materials.edge
      mesh.parent = this.root
      mesh.isPickable = false
      return mesh
    })
    this.supports = Array.from({ length: SUPPORTS }, (_, support) => {
      const mesh = MeshBuilder.CreateBox(`support-rib-${index}-${support}`, { size: 1 }, scene)
      mesh.material = materials.dark
      mesh.parent = this.root
      return mesh
    })
    this.obstacles = Array.from({ length: MAX_OBSTACLES }, (_, obstacle) => (
      new HazardVisual(`hazard-${index}-${obstacle}`, scene, materials, this.root)
    ))
    this.tunnelFrames = Array.from({ length: TUNNEL_FRAMES }, (_, frame) => {
      const mesh = MeshBuilder.CreateTorus(`tunnel-frame-${index}-${frame}`, {
        diameter: 16,
        thickness: 0.24,
        tessellation: 28,
      }, scene)
      mesh.material = frame % 2 === 0 ? materials.edge : materials.dark
      mesh.parent = this.root
      return mesh
    })
    this.root.setEnabled(false)
  }

  public assign(chunk: TrackChunkDefinition, renderOriginZ: number): void {
    this.chunk = chunk
    this.root.setEnabled(true)
    this.root.position.set(0, 0, chunk.startZ - renderOriginZ)
    this.root.rotation.set(0, 0, 0)

    const firstEnd = chunk.gapStart ?? chunk.length
    const secondStart = chunk.gapStart === null ? chunk.length : chunk.gapStart + chunk.gapLength
    updateRibbon(this.platforms[0]!, chunk, 0, firstEnd, -chunk.width * 0.5, chunk.width * 0.5)
    updateRibbon(this.platforms[1]!, chunk, secondStart, chunk.length, -chunk.width * 0.5, chunk.width * 0.5)
    const edgeWidth = 0.18
    updateRibbon(this.rails[0]!, chunk, 0, firstEnd, -chunk.width * 0.5, -chunk.width * 0.5 + edgeWidth)
    updateRibbon(this.rails[1]!, chunk, 0, firstEnd, chunk.width * 0.5 - edgeWidth, chunk.width * 0.5)
    updateRibbon(this.rails[2]!, chunk, secondStart, chunk.length, -chunk.width * 0.5, -chunk.width * 0.5 + edgeWidth)
    updateRibbon(this.rails[3]!, chunk, secondStart, chunk.length, chunk.width * 0.5 - edgeWidth, chunk.width * 0.5)

    for (let index = 0; index < this.supports.length; index += 1) {
      const distance = chunk.length * (index + 0.5) / this.supports.length
      const inGap = chunk.gapStart !== null && distance > chunk.gapStart && distance < chunk.gapStart + chunk.gapLength
      const support = this.supports[index]!
      support.setEnabled(!inGap)
      if (inGap) continue
      const frame = sampleChunkFrame(chunk, distance)
      support.position.set(frame.center.x, frame.center.y - 0.48, frame.center.z - chunk.startZ)
      support.scaling.set(chunk.width * 0.47, 0.34, 0.34)
      support.rotation.set(0, Math.atan2(frame.forward.x, frame.forward.z), chunk.bank)
    }

    for (let index = 0; index < this.obstacles.length; index += 1) this.obstacles[index]!.assign(chunk, index, 0)
    const isTunnel = chunk.archetype === 'tunnel'
    for (let index = 0; index < this.tunnelFrames.length; index += 1) {
      const frameMesh = this.tunnelFrames[index]!
      frameMesh.setEnabled(isTunnel)
      if (!isTunnel) continue
      const distance = chunk.length * (index + 1) / (this.tunnelFrames.length + 1)
      const frame = sampleChunkFrame(chunk, distance)
      frameMesh.position.set(frame.center.x, frame.center.y + 4.2, frame.center.z - chunk.startZ)
      frameMesh.scaling.set(chunk.width / 16, 1, 1)
      frameMesh.rotation.set(Math.PI / 2, Math.atan2(frame.forward.x, frame.forward.z), chunk.bank)
    }
  }

  public update(renderOriginZ: number, tick: number): void {
    if (this.chunk === null) return
    this.root.position.z = this.chunk.startZ - renderOriginZ
    for (let index = 0; index < this.obstacles.length; index += 1) this.obstacles[index]!.assign(this.chunk, index, tick)
  }

  public release(): void {
    this.chunk = null
    for (const obstacle of this.obstacles) obstacle.release()
    this.root.setEnabled(false)
  }

  public setColliderDebug(enabled: boolean): void {
    for (const obstacle of this.obstacles) obstacle.setColliderDebug(enabled)
  }
}

class EnvironmentView {
  private readonly root: TransformNode
  private readonly towers: readonly (Mesh | InstancedMesh)[]
  private readonly accents: readonly (Mesh | InstancedMesh)[]
  private readonly gantries: readonly (Mesh | InstancedMesh)[]

  public constructor(scene: Scene, materials: MaterialLibrary) {
    this.root = new TransformNode('megastructure-field', scene)
    const towerSource = MeshBuilder.CreateBox('megastructure-tower-source', { size: 1 }, scene)
    towerSource.material = materials.dark
    towerSource.parent = this.root
    const accentSource = MeshBuilder.CreateBox('megastructure-accent-source', { size: 1 }, scene)
    accentSource.material = materials.hologram
    accentSource.parent = this.root
    const gantrySource = MeshBuilder.CreateBox('megastructure-gantry-source', { size: 1 }, scene)
    gantrySource.material = materials.dark
    gantrySource.parent = this.root
    this.towers = Array.from({ length: 36 }, (_, index) => index === 0 ? towerSource : towerSource.createInstance(`megastructure-tower-${index}`))
    this.accents = Array.from({ length: 36 }, (_, index) => index === 0 ? accentSource : accentSource.createInstance(`megastructure-accent-${index}`))
    this.gantries = Array.from({ length: 12 }, (_, index) => index === 0 ? gantrySource : gantrySource.createInstance(`megastructure-gantry-${index}`))
    for (let index = 0; index < this.towers.length; index += 1) {
      const side = index % 2 === 0 ? -1 : 1
      const lane = Math.floor(index / 2)
      const height = 15 + (index * 29 % 62)
      const x = side * (27 + index % 6 * 8)
      const z = lane * 62 - 260
      const tower = this.towers[index]!
      tower.position.set(x, height * 0.5 - 8, z)
      tower.scaling.set(2.2 + index % 4, height, 2.8 + index % 5)
      tower.rotation.y = index * 0.31
      const accent = this.accents[index]!
      accent.position.set(x - side * 0.03, height * 0.52 - 8, z - 3.05 - index % 5)
      accent.scaling.set(0.08, Math.max(1.5, height * 0.17), 0.16)
    }
    for (let index = 0; index < this.gantries.length; index += 1) {
      const gantry = this.gantries[index]!
      gantry.position.set(0, 12 + index % 4 * 4, index * 148 - 180)
      gantry.scaling.set(42 + index % 3 * 8, 0.36, 0.55)
      gantry.rotation.z = (index % 2 === 0 ? -1 : 1) * 0.08
    }
  }

  public update(renderOriginZ: number): void {
    this.root.position.z = Math.floor(renderOriginZ / 1_600) * 1_600 - renderOriginZ
  }

  public setVisible(visible: boolean): void {
    this.root.setEnabled(visible)
  }
}

export class WorldView {
  private readonly visuals: readonly ChunkVisual[]
  private readonly assignments = new Map<string, ChunkVisual>()
  private readonly environment: EnvironmentView
  private colliderDebug = false

  public constructor(scene: Scene, materials: MaterialLibrary) {
    this.visuals = Array.from({ length: MAX_VISIBLE_CHUNKS }, (_, index) => new ChunkVisual(index, scene, materials))
    this.environment = new EnvironmentView(scene, materials)
  }

  public sync(chunks: readonly TrackChunkDefinition[], renderOriginZ: number, tick: number): void {
    for (const [id, visual] of this.assignments) {
      if (!chunks.some((chunk) => chunk.id === id)) {
        visual.release()
        this.assignments.delete(id)
      }
    }
    for (const chunk of chunks) {
      let visual = this.assignments.get(chunk.id)
      if (visual === undefined) {
        visual = this.visuals.find((candidate) => candidate.chunk === null)
        if (visual === undefined) continue
        visual.assign(chunk, renderOriginZ)
        visual.setColliderDebug(this.colliderDebug)
        this.assignments.set(chunk.id, visual)
      }
      visual.update(renderOriginZ, tick)
    }
    this.environment.update(renderOriginZ)
  }

  public setVisible(visible: boolean): void {
    for (const visual of this.visuals) {
      if (visual.chunk !== null) visual.update(0, 0)
    }
    this.environment.setVisible(visible)
  }

  public setColliderDebug(enabled: boolean): void {
    this.colliderDebug = enabled
    for (const visual of this.visuals) visual.setColliderDebug(enabled)
  }
}
